import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Divider,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import SharesIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';
import ContactsIcon from '@mui/icons-material/Contacts';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadEmail } from '../../contexts/UnreadEmailContext';

const DRAWER_WIDTH = 260;

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
}

const navItemsConfig: Array<{ label: string; icon: React.ReactNode; path: string; badge?: number }> = [
  { label: 'Shares', icon: <SharesIcon />, path: '/' },
  { label: 'Documents', icon: <DescriptionIcon />, path: '/documents' },
  { label: 'Contacts', icon: <ContactsIcon />, path: '/contacts' },
  { label: 'Search', icon: <SearchIcon />, path: '/search' },
  { label: 'Emails', icon: <EmailIcon />, path: '/emails' },
  { label: 'AI', icon: <AutoAwesomeIcon />, path: '/ai' },
  { label: 'Bots', icon: <PrecisionManufacturingIcon />, path: '/bots' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

export default function NavDrawer({ open, onClose }: NavDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin } = useAuth();
  const { unreadCount } = useUnreadEmail();

  const navItems = navItemsConfig.map((item) =>
    item.path === '/emails' ? { ...item, badge: unreadCount } : item
  );

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNav(item.path)}
              sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
            >
              <ListItemIcon>
                {'badge' in item && item.badge! > 0
                  ? <Badge badgeContent={item.badge} color="error">{item.icon}</Badge>
                  : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAdmin && (
        <>
          <Divider />
          <List subheader={<ListSubheader>Admin</ListSubheader>}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === '/admin/users'}
                onClick={() => handleNav('/admin/users')}
                sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
              >
                <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary="Users" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === '/admin/status'}
                onClick={() => handleNav('/admin/status')}
                sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
              >
                <ListItemIcon><MonitorHeartIcon /></ListItemIcon>
                <ListItemText primary="Status" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
      <Divider sx={{ mt: 'auto' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={logout}
            sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
          >
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}
